import { useCallback, useEffect, useRef, useState } from 'react';

type UseAutoLoadMoreOptions = {
  enabled: boolean;
  cursor?: string | null;
  onLoadMore: () => Promise<unknown> | void;
  rootMargin?: string;
};

const useAutoLoadMore = ({
  enabled,
  cursor,
  onLoadMore,
  rootMargin = '200px 0px',
}: UseAutoLoadMoreOptions) => {
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const lastTriggeredCursorRef = useRef<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const triggerLoadMore = useCallback(() => {
    if (!enabled || isLoadingMore || lastTriggeredCursorRef.current === (cursor ?? null)) {
      return;
    }

    lastTriggeredCursorRef.current = cursor ?? null;
    setIsLoadingMore(true);

    void Promise.resolve(onLoadMore())
      .catch(() => {
        lastTriggeredCursorRef.current = null;
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [cursor, enabled, isLoadingMore, onLoadMore]);

  useEffect(() => {
    if (!enabled) {
      lastTriggeredCursorRef.current = null;
      return;
    }

    const node = loadMoreRef.current;

    if (!node || isLoadingMore || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          triggerLoadMore();
        }
      },
      { rootMargin },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [enabled, isLoadingMore, rootMargin, triggerLoadMore]);

  return {
    isLoadingMore,
    loadMoreRef,
    triggerLoadMore,
  };
};

export default useAutoLoadMore;
